import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherChatView.css';

function TeacherChatView({ teacherData }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateChat, setShowCreateChat] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [chatType, setChatType] = useState('parent');
  const [creating, setCreating] = useState(false);

  const loadConversations = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    console.log('🔥 TeacherChat: Loading conversations for teacher', teacherData.uid);
    try {
      // Load conversations where teacherId matches the logged-in teacher
      const teacherConversationsQuery = query(
        collection(db, 'teacherParentConversations'),
        where('teacherId', '==', teacherData.uid)
      );
      const teacherSnapshot = await getDocs(teacherConversationsQuery);
      const teacherConversations = teacherSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Admin conversations where THIS teacher is the inquirer — teacher→admin
      // convos put the sender in parentId (both web and iOS conventions).
      const adminConversationsQuery = query(
        collection(db, 'teacherParentConversations'),
        where('conversationType', '==', 'admin'),
        where('parentId', '==', teacherData.uid)
      );
      const adminSnapshot = await getDocs(adminConversationsQuery);
      const adminConversations = adminSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Combine own teacher convos + own admin inquiries (dedupe by doc id).
      // Dedupe by doc id — a teacher-created admin convo matches BOTH queries.
      const seen = new Set();
      const allConversations = [...teacherConversations, ...adminConversations].filter(c => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      console.log('🔥 TeacherChat: Conversations loaded', allConversations.length, allConversations);
      setConversations(allConversations);
    } catch (error) {
      console.error('🔥 TeacherChat: Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  const loadMessages = useCallback(async (conversationId) => {
    console.log('🔥 TeacherChat: Loading messages for conversation', conversationId);
    try {
      // Use teacherParentConversations/{conversationId}/messages subcollection (like iOS)
      const messagesQuery = query(
        collection(db, 'teacherParentConversations', conversationId, 'messages')
      );
      const snapshot = await getDocs(messagesQuery);
      const messagesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      messagesData.sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));
      console.log('🔥 TeacherChat: Messages loaded', messagesData.length, messagesData);
      setMessages(messagesData);
    } catch (error) {
      console.error('🔥 TeacherChat: Error loading messages:', error);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [teacherData, loadConversations]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation, loadMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const isAdminConvo = selectedConversation.conversationType === 'admin';
    const now = new Date().toISOString();

    try {
      // Use teacherParentConversations collection (like iOS).
      // iOS strictly decodes messages — every field below must be present and a String.
      // senderRole must be a Title-case UserRole raw value ("Teacher", "Parent", "Admin").
      await addDoc(collection(db, 'teacherParentConversations', selectedConversation.id, 'messages'), {
        conversationId: selectedConversation.id,
        senderId: teacherData?.uid || '',
        senderName: teacherData?.displayName || 'Teacher',
        senderRole: 'Teacher',
        // Admin convos have no specific recipient user — use the 'admin' sentinel.
        recipientId: isAdminConvo ? 'admin' : (selectedConversation.parentId || ''),
        recipientName: isAdminConvo ? 'Admin' : (selectedConversation.parentName || ''),
        message: newMessage,
        studentId: selectedConversation.studentId || '',
        studentName: selectedConversation.studentName || 'General Inquiry',
        isRead: false,
        sentAt: now
      });

      // Match iOS ChatService.updateTeacherParentConversation: bump unreadCount
      // for the recipient and stamp lastMessageSenderId (ISO string dates).
      await updateDoc(doc(db, 'teacherParentConversations', selectedConversation.id), {
        lastMessage: newMessage,
        lastMessageAt: now,
        lastMessageSenderId: teacherData?.uid || '',
        unreadCount: increment(1)
      });

      setNewMessage('');
      loadMessages(selectedConversation.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  const loadClassesForChat = async () => {
    if (!teacherData?.uid) return;
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClasses(classesData);
      if (classesData.length > 0) {
        setSelectedClassId(classesData[0].id);
        loadStudentsForChat(classesData[0].id);
      }
    } catch (error) {
      console.error('Error loading classes for chat:', error);
    }
  };

  const loadStudentsForChat = async (classId) => {
    if (!classId) return;
    try {
      const studentsQuery = query(
        collection(db, 'classes', classId, 'students'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(studentsQuery);
      const studentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(studentsData);
      setSelectedStudent(null);
    } catch (error) {
      console.error('Error loading students for chat:', error);
    }
  };

  const findParentForStudent = async (studentId) => {
    try {
      const parentsQuery = query(
        collection(db, 'parents'),
        where('linkedChildrenIds', 'array-contains', studentId)
      );
      const snapshot = await getDocs(parentsQuery);
      if (!snapshot.empty) {
        const parentData = snapshot.docs[0].data();
        return { id: snapshot.docs[0].id, ...parentData };
      }
      return null;
    } catch (error) {
      console.error('Error finding parent:', error);
      return null;
    }
  };

  const handleCreateChat = async () => {
    if (!teacherData?.uid) return;
    if (chatType === 'parent' && !selectedStudent) {
      alert('Please select a student');
      return;
    }

    setCreating(true);
    try {
      if (chatType === 'admin') {
        // Create or find admin conversation
        const adminQuery = query(
          collection(db, 'teacherParentConversations'),
          where('conversationType', '==', 'admin'),
          where('teacherId', '==', teacherData.uid)
        );
        const adminSnapshot = await getDocs(adminQuery);
        if (!adminSnapshot.empty) {
          const existingConv = { id: adminSnapshot.docs[0].id, ...adminSnapshot.docs[0].data() };
          setConversations(prev => {
            const filtered = prev.filter(c => c.id !== existingConv.id);
            return [existingConv, ...filtered];
          });
          setSelectedConversation(existingConv);
        } else {
          // iOS strictly decodes conversations — teacherId/teacherName/parentId/
          // parentName/studentId/studentName must ALL be present Strings, and
          // conversationType must be 'teacher' or 'admin' (ConversationType enum).
          // For teacher→admin convos the teacher occupies the 'parent' slot
          // (the non-admin inquirer), matching iOS startConversationWithAdmin.
          const now = new Date().toISOString();
          const adminConvData = {
            teacherId: teacherData.uid,
            teacherName: teacherData?.displayName || 'Teacher',
            parentId: teacherData.uid,
            parentName: teacherData?.displayName || 'Teacher',
            studentId: '',
            studentName: 'General Inquiry',
            conversationType: 'admin',
            lastMessage: '',
            lastMessageAt: now,
            lastMessageSenderId: teacherData.uid,
            unreadCount: 0,
            createdAt: now
          };
          const newConv = await addDoc(collection(db, 'teacherParentConversations'), adminConvData);
          const convData = { id: newConv.id, ...adminConvData };
          setConversations(prev => [convData, ...prev]);
          setSelectedConversation(convData);
        }
      } else {
        // Parent chat
        const parent = await findParentForStudent(selectedStudent.id);
        if (!parent) {
          alert('No parent found for this student');
          setCreating(false);
          return;
        }

        // Check if conversation already exists
        const existingQuery = query(
          collection(db, 'teacherParentConversations'),
          where('teacherId', '==', teacherData.uid),
          where('parentId', '==', parent.id),
          where('studentId', '==', selectedStudent.id)
        );
        const existingSnapshot = await getDocs(existingQuery);
        if (!existingSnapshot.empty) {
          const existingConv = { id: existingSnapshot.docs[0].id, ...existingSnapshot.docs[0].data() };
          setConversations(prev => {
            const filtered = prev.filter(c => c.id !== existingConv.id);
            return [existingConv, ...filtered];
          });
          setSelectedConversation(existingConv);
        } else {
          // iOS ConversationType raw values are 'teacher'/'admin' — never 'teacher-parent'.
          const now = new Date().toISOString();
          const parentConvData = {
            teacherId: teacherData.uid,
            teacherName: teacherData?.displayName || 'Teacher',
            parentId: parent.id,
            parentName: parent.fullName || `${parent.firstName} ${parent.lastName}`,
            studentId: selectedStudent.id,
            studentName: selectedStudent.fullName || '',
            conversationType: 'teacher',
            lastMessage: '',
            lastMessageAt: now,
            lastMessageSenderId: teacherData.uid,
            unreadCount: 0,
            createdAt: now
          };
          const newConv = await addDoc(collection(db, 'teacherParentConversations'), parentConvData);
          const convData = { id: newConv.id, ...parentConvData };
          setConversations(prev => [convData, ...prev]);
          setSelectedConversation(convData);
        }
      }
      setShowCreateChat(false);
      setSelectedStudent(null);
      setChatType('parent');
    } catch (error) {
      console.error('Error creating chat:', error);
      alert('Failed to create chat');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="teacher-chat-view">
      <div className="view-header">
        <h2>Parent Chat</h2>
      </div>

      {!selectedConversation ? (
        <>
          <div className="chat-list-header">
            <p className="count-text">Conversations: {conversations.length}</p>
            <button
              className="create-chat-button"
              onClick={() => {
                loadClassesForChat();
                setShowCreateChat(true);
              }}
            >
              + New Chat
            </button>
          </div>

          {conversations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💬</div>
              <h3>No Conversations</h3>
              <p>Conversations with parents will appear here</p>
            </div>
          ) : (
            <div className="conversations-list">
              {conversations.map(conv => (
                <div
                  key={conv.id}
                  className="conversation-card"
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="conversation-icon">💬</div>
                  <div className="conversation-info">
                    <h4>{conv.conversationType === 'admin' && conv.parentId === teacherData?.uid ? 'Admin' : conv.parentName}</h4>
                    <p className="last-message">{conv.lastMessage || 'No messages yet'}</p>
                    <p className="last-time">{conv.lastMessageAt ? formatDate(conv.lastMessageAt) : ''}</p>
                  </div>
                  {conv.unreadCount > 0 && (
                    <div className="unread-badge">{conv.unreadCount}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {showCreateChat && (
            <div className="create-chat-overlay" onClick={() => setShowCreateChat(false)}>
              <div className="create-chat-modal" onClick={(e) => e.stopPropagation()}>
                <div className="create-chat-header">
                  <h3>Start New Chat</h3>
                  <button className="close-button" onClick={() => setShowCreateChat(false)}>
                    ×
                  </button>
                </div>
                <div className="create-chat-content">
                  <div className="chat-type-selector">
                    <button
                      className={`chat-type-button ${chatType === 'parent' ? 'active' : ''}`}
                      onClick={() => setChatType('parent')}
                    >
                      Parent
                    </button>
                    <button
                      className={`chat-type-button ${chatType === 'admin' ? 'active' : ''}`}
                      onClick={() => setChatType('admin')}
                    >
                      Admin
                    </button>
                  </div>

                  {chatType === 'parent' && (
                    <>
                      <div className="form-group">
                        <label>Select Class</label>
                        <select
                          value={selectedClassId}
                          onChange={(e) => {
                            setSelectedClassId(e.target.value);
                            loadStudentsForChat(e.target.value);
                          }}
                        >
                          {classes.map(cls => (
                            <option key={cls.id} value={cls.id}>
                              {cls.name} ({cls.grade})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Select Student</label>
                        <select
                          value={selectedStudent?.id || ''}
                          onChange={(e) => {
                            const student = students.find(s => s.id === e.target.value);
                            setSelectedStudent(student || null);
                          }}
                        >
                          <option value="">Select a student</option>
                          {students.map(student => (
                            <option key={student.id} value={student.id}>
                              {student.fullName} (ID: {student.studentId})
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {chatType === 'admin' && (
                    <div className="form-group">
                      <p className="info-text">Start a conversation with the school admin.</p>
                    </div>
                  )}

                  <button
                    className="create-button"
                    onClick={handleCreateChat}
                    disabled={creating || (chatType === 'parent' && !selectedStudent)}
                  >
                    {creating ? 'Creating...' : 'Start Chat'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="chat-detail">
          <div className="chat-header">
            <button className="back-button" onClick={() => setSelectedConversation(null)}>
              ← Back
            </button>
            <h3>{selectedConversation.conversationType === 'admin' && selectedConversation.parentId === teacherData?.uid ? 'Admin' : selectedConversation.parentName}</h3>
          </div>

          <div className="messages-container">
            {messages.length === 0 ? (
              <div className="empty-state">
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  className={`message ${msg.senderId === teacherData?.uid || (msg.senderRole || '').toLowerCase() === 'teacher' ? 'sent' : 'received'}`}
                >
                  <div className="message-content">
                    <p>{msg.message}</p>
                    <span className="message-time">{formatTime(msg.sentAt)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <form className="message-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="message-input"
            />
            <button type="submit" className="send-button">
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default TeacherChatView;
